import { COLORS } from '../../constants/theme';
import { useGatherIntel } from '../../hooks/useGatherIntel';
import { IntelFreshnessIndicator } from '../attack/IntelFreshnessIndicator';
import { formatEffectDuration } from '../../utils/effectDuration';
import { formatNumber, formatEnumLabel } from '../../utils/format';
import { raceChip, personalityChip } from '../../utils/dossierFlavor';
import { DossierFlavorChip } from '../dossier/DossierFlavorChip';
import type { CrystalBallData, IntelReportDto } from '../../types/intel';

const CB_FG = '#b89fe0';
const OFF_CLR = '#e8a04f';

interface MobileCrystalBallCardProps {
  worldId: string;
  playerProvinceId: string;
  targetProvinceId: string;
  data: CrystalBallData | null;
  report: IntelReportDto | null;
  isLoading: boolean;
  error: string | null;
  onResult: (message: string, type: 'success' | 'failure' | 'warning') => void;
  onGathered: () => void;
  /** SF-157: per-report Share/Delete, gated on report.sharing === 'PRIVATE' for Share. Omitted
   *  (undefined/falsy) by Target Finder's existing usage — no icons render there. */
  onShare?: () => void;
  onDelete?: () => void;
  /** SF-157: when true, suppresses only the Gather/Refresh button and its trigger — the
   *  `useGatherIntel` hook itself is still called unconditionally (Rules of Hooks). Renders all
   *  other content (Province/Personality/Networth/Military Roster/etc.) exactly as it does live. */
  readOnly?: boolean;
}

/**
 * SF-171 Component 9: Crystal Ball mobile card. Bold Offense/Defense Points headline, then
 * single-column grouped detail (Province, Resources, Military Roster, Development, Effects) — one
 * fact per row, dividers between groups. Never receives/uses a `disabled` prop — Crystal Ball is
 * exempt from SF-105 protection gating (mirrors `IntelButtons.tsx`'s existing exemption).
 */
export function MobileCrystalBallCard({
  worldId, playerProvinceId, targetProvinceId, data, report, isLoading, error, onResult, onGathered,
  onShare, onDelete, readOnly,
}: MobileCrystalBallCardProps) {
  const { gather, state } = useGatherIntel(worldId, playerProvinceId, targetProvinceId, { onResult, onGathered });
  const isGathering = state.CB === 'loading';

  return (
    <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 10, opacity: readOnly ? 0.85 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'rgba(107,79,160,0.15)', borderBottom: `1px solid ${COLORS.border}` }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: CB_FG, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Crystal Ball</span>
          {readOnly && (
            <span style={{ fontSize: 9, color: COLORS.text.muted, border: `1px solid ${COLORS.border}`, borderRadius: 3, padding: '1px 4px', textTransform: 'uppercase' }}>
              Archived
            </span>
          )}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {report && <IntelFreshnessIndicator capturedAt={report.capturedAt} />}
          {onShare && report?.sharing === 'PRIVATE' && (
            <button
              type="button"
              aria-label="Share this Crystal Ball report"
              onClick={onShare}
              style={{ minHeight: 44, minWidth: 32, background: 'none', border: 'none', color: COLORS.positive, cursor: 'pointer', fontSize: 14 }}
            >
              ⤴
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              aria-label="Delete this Crystal Ball report"
              onClick={onDelete}
              style={{ minHeight: 44, minWidth: 32, background: 'none', border: 'none', color: COLORS.negative, cursor: 'pointer', fontSize: 14 }}
            >
              🗑
            </button>
          )}
          {data && !readOnly && (
            <button
              type="button"
              aria-label="Refresh Crystal Ball intel"
              onClick={() => gather('CB')}
              disabled={isGathering}
              style={{ minHeight: 44, minWidth: 44, background: 'none', border: 'none', color: COLORS.link, cursor: isGathering ? 'wait' : 'pointer', fontSize: 16 }}
            >
              ⟳
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: 12 }}>
        {isLoading && !data && (
          <p style={{ color: COLORS.text.muted, fontSize: 12, margin: 0 }}>Loading...</p>
        )}
        {error && !data && (
          <p style={{ color: COLORS.negative, fontSize: 12, margin: 0 }}>Failed to load Crystal Ball intel.</p>
        )}
        {!isLoading && !error && !data && (
          <>
            <p style={{ color: COLORS.text.muted, fontSize: 12, marginTop: 0 }}>
              Reveals resources, military units, and offense/defense points.
            </p>
            <button
              type="button"
              onClick={() => gather('CB')}
              disabled={isGathering}
              style={{
                width: '100%', minHeight: 44, borderRadius: 6, border: 'none',
                background: COLORS.btnPrimary, color: '#e8ecf4', fontSize: 13, fontWeight: 700,
                cursor: isGathering ? 'wait' : 'pointer',
              }}
            >
              {isGathering ? 'Casting...' : 'Gather Crystal Ball'}
            </button>
          </>
        )}
        {data && (
          <>
            <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
              <Headline label="Offense Points" value={formatNumber(data.offensePoints)} color={OFF_CLR} />
              <Headline label="Defense Points" value={formatNumber(data.defensePoints)} color={COLORS.link} />
            </div>

            <GroupLabel text="Province" />
            <div style={{ display: 'flex', gap: 4, padding: '4px 0' }}>
              <DossierFlavorChip meta={raceChip(data.race)} />
              <DossierFlavorChip meta={personalityChip(data.personality)} />
            </div>
            <LV label="Ruler" value={data.rulerName} />
            <LV label="Total Land (acres)" value={formatNumber(data.totalLand)} />
            <LV label="Building Efficiency (%)" value={`${Math.round(data.buildingEfficiency * 100)}`} />
            {data.networth != null && <LV label="Networth" value={formatNumber(data.networth)} color={COLORS.gold} />}
            {data.kingdomStance && <LV label="Kingdom Stance" value={formatEnumLabel(data.kingdomStance)} />}
            {data.dragonType && <LV label="Dragon" value={formatEnumLabel(data.dragonType)} />}
            {data.atWarWith.length > 0 && <LV label="At War With" value={data.atWarWith.join(', ')} />}

            <GroupLabel text="Resources" />
            <LV label="Gold (gc)" value={formatNumber(data.money)} color={COLORS.gold} />
            <LV label="Food (bushels)" value={formatNumber(data.food)} />
            <LV label="Runes" value={formatNumber(data.runes)} color={CB_FG} />
            <LV
              label="Trade Balance (gc)"
              value={`${data.tradeBalance >= 0 ? '+' : ''}${formatNumber(data.tradeBalance)}`}
              color={data.tradeBalance >= 0 ? COLORS.positive : '#d48060'}
            />

            <GroupLabel text="Military Roster" />
            <LV label="Peasants" value={formatNumber(data.peasants)} />
            <LV label="Soldiers" value={formatNumber(data.soldiers)} />
            <LV label="Offense Specialists" value={formatNumber(data.offenseSpecialists)} />
            <LV label="Defense Specialists" value={formatNumber(data.defenseSpecialists)} />
            <LV label="Elites" value={formatNumber(data.elites)} />
            <LV label="War Horses" value={formatNumber(data.warHorses)} />
            <LV label="Prisoners" value={formatNumber(data.prisoners)} />
            <LV label="Thieves" value="Hidden" muted />
            <LV label="Wizards" value="Hidden" muted />

            {data.activeEffects.length > 0 && (
              <>
                <GroupLabel text="Effects" />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {data.activeEffects.map((e, i) => (
                    <span
                      key={i}
                      style={{
                        display: 'inline-block', padding: '2px 6px', borderRadius: 3, fontSize: 10,
                        background: 'rgba(107,79,160,0.2)', color: '#c4b0e8', border: '1px solid rgba(107,79,160,0.3)',
                      }}
                    >
                      {formatEnumLabel(e.effectType)} ({formatEffectDuration(e)})
                    </span>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Headline({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ flex: 1, background: COLORS.cardAlt, borderRadius: 6, padding: '8px 10px' }}>
      <div style={{ color: COLORS.text.tertiary, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ color, fontSize: 18, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>{value}</div>
    </div>
  );
}

function GroupLabel({ text }: { text: string }) {
  return (
    <div style={{ color: COLORS.text.tertiary, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '10px 0 4px', borderTop: `1px solid ${COLORS.border}`, paddingTop: 8 }}>
      {text}
    </div>
  );
}

function LV({ label, value, color, muted }: { label: string; value: string; color?: string; muted?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
      <span style={{ color: COLORS.text.tertiary, fontSize: 12 }}>{label}</span>
      <span style={{ color: muted ? COLORS.text.muted : (color ?? COLORS.text.primary), fontSize: 12, fontStyle: muted ? 'italic' : 'normal', fontFamily: "'JetBrains Mono', monospace" }}>
        {value}
      </span>
    </div>
  );
}
